import React, { useState, useEffect } from 'react';
import { Modal } from '../../../shared/components/Modal';
import { Button } from '../../../shared/components/Button';
import { PageSettings } from '../../../core/ast/document.types';
import { Sliders, FileSpreadsheet, Type, AlignLeft } from 'lucide-react';

interface PageSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  pageSettings: PageSettings;
  onSave: (updates: Partial<PageSettings>) => void;
}

export const PageSettingsModal: React.FC<PageSettingsModalProps> = ({
  isOpen,
  onClose,
  pageSettings,
  onSave,
}) => {
  const [settings, setSettings] = useState<PageSettings>(pageSettings);

  useEffect(() => {
    setSettings(pageSettings);
  }, [pageSettings, isOpen]);

  const handleApply = () => {
    onSave(settings);
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Page Layout & Document Settings"
      maxWidth="540px"
      footer={
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--space-2)' }}>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleApply}>
            Apply Settings
          </Button>
        </div>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
        {/* Paper & Orientation */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
          <span style={{ fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', color: 'var(--primary-600)', display: 'flex', alignItems: 'center', gap: 4 }}>
            <FileSpreadsheet size={13} /> Paper & Orientation
          </span>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-2)' }}>
            <div className="form-group">
              <label className="form-label">Paper Format</label>
              <select
                className="form-select"
                value={settings.paperSize}
                onChange={(e) => setSettings((s) => ({ ...s, paperSize: e.target.value as any }))}
              >
                <option value="A4">A4 (210 × 297 mm)</option>
                <option value="Letter">US Letter (8.5 × 11 in)</option>
                <option value="Legal">US Legal (8.5 × 14 in)</option>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Orientation</label>
              <select
                className="form-select"
                value={settings.orientation}
                onChange={(e) => setSettings((s) => ({ ...s, orientation: e.target.value as any }))}
              >
                <option value="portrait">Portrait</option>
                <option value="landscape">Landscape</option>
              </select>
            </div>
          </div>
        </div>

        {/* Margins */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
          <span style={{ fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', color: 'var(--primary-600)', display: 'flex', alignItems: 'center', gap: 4 }}>
            <Sliders size={13} /> Margins (mm)
          </span>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 'var(--space-2)' }}>
            <div className="form-group">
              <label className="form-label" style={{ fontSize: '10px' }}>Top</label>
              <input
                type="number"
                className="form-input text-xs"
                value={settings.margins.topMm}
                onChange={(e) => setSettings((s) => ({ ...s, margins: { ...s.margins, topMm: Number(e.target.value) } }))}
              />
            </div>
            <div className="form-group">
              <label className="form-label" style={{ fontSize: '10px' }}>Bottom</label>
              <input
                type="number"
                className="form-input text-xs"
                value={settings.margins.bottomMm}
                onChange={(e) => setSettings((s) => ({ ...s, margins: { ...s.margins, bottomMm: Number(e.target.value) } }))}
              />
            </div>
            <div className="form-group">
              <label className="form-label" style={{ fontSize: '10px' }}>Left</label>
              <input
                type="number"
                className="form-input text-xs"
                value={settings.margins.leftMm}
                onChange={(e) => setSettings((s) => ({ ...s, margins: { ...s.margins, leftMm: Number(e.target.value) } }))}
              />
            </div>
            <div className="form-group">
              <label className="form-label" style={{ fontSize: '10px' }}>Right</label>
              <input
                type="number"
                className="form-input text-xs"
                value={settings.margins.rightMm}
                onChange={(e) => setSettings((s) => ({ ...s, margins: { ...s.margins, rightMm: Number(e.target.value) } }))}
              />
            </div>
          </div>
        </div>

        {/* Typography */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
          <span style={{ fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', color: 'var(--primary-600)', display: 'flex', alignItems: 'center', gap: 4 }}>
            <Type size={13} /> Default Typography
          </span>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-2)' }}>
            <div className="form-group">
              <label className="form-label">Font Family</label>
              <select
                className="form-select"
                value={settings.defaultFontFamily || 'Times New Roman'}
                onChange={(e) => setSettings((s) => ({ ...s, defaultFontFamily: e.target.value }))}
              >
                <option value="Times New Roman">Times New Roman (Academic)</option>
                <option value="Inter">Inter (Sans-serif)</option>
                <option value="Arial">Arial</option>
                <option value="Computer Modern">Computer Modern (LaTeX)</option>
                <option value="JetBrains Mono">JetBrains Mono (Code)</option>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Font Size</label>
              <select
                className="form-select"
                value={settings.defaultFontSizePt || 12}
                onChange={(e) => setSettings((s) => ({ ...s, defaultFontSizePt: Number(e.target.value) }))}
              >
                <option value={10}>10 pt (Compact)</option>
                <option value={11}>11 pt (Standard)</option>
                <option value={12}>12 pt (Academic Formal)</option>
              </select>
            </div>
          </div>
          <div className="form-group">
            <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <AlignLeft size={12} /> Line Spacing
            </label>
            <select
              className="form-select"
              value={settings.defaultLineSpacing || 1.5}
              onChange={(e) => setSettings((s) => ({ ...s, defaultLineSpacing: Number(e.target.value) }))}
            >
              <option value={1.15}>1.15 (Single / Tight)</option>
              <option value={1.3}>1.3 (Compact Academic)</option>
              <option value={1.5}>1.5 (Standard 1.5 Spacing)</option>
              <option value={2.0}>2.0 (Double Spacing)</option>
            </select>
          </div>
        </div>
      </div>
    </Modal>
  );
};
